import { twMerge } from 'tailwind-merge';

import { QueryMenu } from '@/sections/insights/queryBuilder/components/QueryBuilderSidebar/QueryMenu';
import type { QueryFolderType } from '@/sections/insights/queryBuilder/savedQueries/folders';
import { SavedQuery } from '@/sections/insights/queryBuilder/types/SavedQuery';

export interface QueryFolderContentProps {
  queries: SavedQuery[];
  isExpanded: boolean;
  folderId: QueryFolderType;
  isCustomQueriesFolder: boolean;
  selectedQueryId: string | null;
  onQueryClick: (queryId: string) => void;
  onNewQueryClick: () => void;
}

/**
 * QueryFolderContent - Content component for QueryFolder
 *
 * Renders the collapsible folder content with:
 * - CSS Grid animation wrapper
 * - New Query button (Custom Queries folder only)
 * - List of queries
 * - Empty state (when no queries and not Custom Queries folder)
 */
export const QueryFolderContent = ({
  queries,
  isExpanded,
  folderId,
  isCustomQueriesFolder,
  selectedQueryId,
  onQueryClick,
  onNewQueryClick,
}: QueryFolderContentProps) => {
  return (
    <div
      className={twMerge(
        'border-chariot-stroke grid border-b transition-[grid-template-rows,opacity] duration-300 ease-in-out',
        isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
      )}
      data-testid={`folder-content-${folderId}`}
    >
      <div className="overflow-hidden">
        {queries.length === 0 && !isCustomQueriesFolder ? (
          <div className="text-chariot-text-secondary px-6 py-4 text-sm">
            No queries in this folder
          </div>
        ) : (
          <div className="overflow-visible py-1">
            {/* Show New Query button at the top of Custom Queries folder */}
            {isCustomQueriesFolder && (
              <NewQueryButton onClick={onNewQueryClick} />
            )}

            {/* Render existing queries */}
            {queries.map(query => (
              <FolderQuery
                key={query.id}
                query={query}
                onQueryClick={onQueryClick}
                isSelected={selectedQueryId === query.id}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

interface FolderQueryProps {
  query: SavedQuery;
  onQueryClick: (queryId: string) => void;
  isSelected: boolean;
}

const FolderQuery = ({ query, onQueryClick, isSelected }: FolderQueryProps) => {
  return (
    <div
      key={query.id}
      role="button"
      tabIndex={0}
      onClick={() => onQueryClick(query.id)}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onQueryClick(query.id);
        }
      }}
      className={`hover:bg-chariot-background-secondary group flex w-full cursor-pointer items-center justify-between px-6 py-3 transition-colors ${
        isSelected ? 'bg-chariot-background-tertiary' : ''
      }`}
      data-testid={`query-item-${query.id}`}
      aria-label={`Open query: ${query.name}`}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="text-chariot-text-primary text-sm font-medium">
          {query.name}
        </span>
        {query.description && (
          <span className="text-chariot-text-secondary line-clamp-2 text-xs">
            {query.description}
          </span>
        )}
      </div>

      {/* Three-dot menu - only visible on hover */}
      <div className="ml-2 opacity-0 transition-opacity group-hover:opacity-100">
        <QueryMenu query={query} />
      </div>
    </div>
  );
};

interface NewQueryButtonProps {
  onClick: () => void;
}

const NewQueryButton = ({ onClick }: NewQueryButtonProps) => {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className="hover:bg-chariot-background-secondary group flex w-full cursor-pointer items-center justify-between px-6 py-3 transition-colors"
      data-testid="new-query-button"
      aria-label="Create new query"
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="text-chariot-brand-primary text-sm font-medium">
          + New Query
        </span>
        <span className="text-chariot-text-secondary text-xs">
          Create a new custom query
        </span>
      </div>
    </div>
  );
};
